/* ══════════════════════════════════════════════════════════════════
   Leisten, die zur Seite rollen — und der gewählte Punkt darin.

   Am Telefon brechen Reiter, Umschalter und Filter nicht mehr um, sie
   rollen zur Seite (`stile/telefon.css`, „Reiter und Filter"). **Gemessen
   am 17.09.2026 auf 390 px:** Die Ressourcen trugen zwei Zeilen Reiter
   (90 px) und vier Zeilen Pillen (248 px), bevor die erste Maschine kam.
   Als rollende Reihen sind es 44 und 67 px.

   Damit gibt es zum ersten Mal einen gewählten Punkt, der **außerhalb des
   Bildes** stehen kann. Der Katalog von Preisstamm und Weiteres hatte das
   als Erster (`masken/stamm/katalogrolle.ts`): `/weiteres?blatt=zahlungsziele`
   springt auf das letzte von sechs Blättern, und die Leiste stünde ganz
   links — sie behauptete dann, es seien die Gerätegruppen.

   Die Rechnung von dort steht jetzt hier, für jede Leiste:

   * `insBild(leiste, punkt)` — rückt einen Punkt in den sichtbaren Teil.
     **Nur waagerecht** (`scrollLeft` von Hand): `scrollIntoView` rückte
     die ganze Maske zurecht, und man sähe als Erstes, wie das Blatt unter
     dem Kopf wegrutscht.
   * `rollleistenFolgen()` — horcht am Dokument, ob irgendwo ein Punkt
     gewählt wird (`aria-pressed`, `aria-selected`, `aria-current`), und
     rückt ihn in seine Leiste, **wenn die überhaupt rollt**. Eine Maske
     schreibt dafür nichts: Sie setzt ihre Wahl so, wie sie es für den
     Vorleser ohnehin tut.

   **Nur am Telefon.** Am Schreibtisch brechen die Leisten um, und ein
   Rücken, das niemand bestellt hat, ist eine Bewegung, die stört.

   * `useRollrand(ref)` — misst, ob **links oder rechts noch etwas steht**,
     damit die Leiste es zeigen kann (20.09.2026, siehe
     `bausteine/Reiterleiste.tsx`). Gemessen, nicht geraten: aus
     `scrollLeft`, `scrollWidth`, `clientWidth`, bei jedem Rollen, jeder
     Größenänderung und jedem Reiter, der dazukommt oder breiter wird.
   ══════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from "react";

/** Was einen gewählten Punkt ausmacht — dieselben Träger, die ein Vorleser liest. */
const GEWAEHLT = '[aria-pressed="true"], [aria-selected="true"], [aria-current="page"], [aria-current="true"]';

/**
 * Rückt `punkt` in den sichtbaren Teil von `leiste` — mit einem Finger breit
 * Luft daneben: Ein Punkt, der genau an der Kante endet, sieht abgeschnitten
 * aus, auch wenn er ganz da ist.
 */
export function insBild(leiste: HTMLElement, punkt: HTMLElement, luft = 16): void {
  // Kein Überlauf heißt: Die Leiste bricht um oder steht als Spalte da.
  if (leiste.scrollWidth <= leiste.clientWidth + 1) return;
  const rahmen = leiste.getBoundingClientRect();
  const lage = punkt.getBoundingClientRect();
  const links = lage.left - rahmen.left + leiste.scrollLeft;
  const rechts = links + lage.width;
  if (links - luft < leiste.scrollLeft) {
    leiste.scrollLeft = Math.max(0, links - luft);
  } else if (rechts + luft > leiste.scrollLeft + leiste.clientWidth) {
    leiste.scrollLeft = rechts + luft - leiste.clientWidth;
  }
}

/** Die nächste Leiste über dem Punkt, die zur Seite rollt — höchstens vier Ebenen hoch. */
function leisteVon(punkt: HTMLElement): HTMLElement | null {
  let el = punkt.parentElement;
  for (let tiefe = 0; el && tiefe < 4; tiefe += 1, el = el.parentElement) {
    const x = getComputedStyle(el).overflowX;
    if ((x === "auto" || x === "scroll") && el.scrollWidth > el.clientWidth + 1) return el;
  }
  return null;
}

/**
 * Rückt am Telefon jeden gewählten Punkt in seine Leiste, sobald er gewählt
 * oder gezeichnet wird. Einmal beim Start (`main.tsx`); gibt das Abmelden zurück.
 */
export function rollleistenFolgen(): () => void {
  if (typeof MutationObserver === "undefined" || typeof document === "undefined") return () => {};
  const telefon = () => document.documentElement.dataset["geraet"] === "telefon";
  const offen = new Set<HTMLElement>();
  let geplant = false;

  // Gerückt wird im nächsten Bild und für alle auf einmal: Beim Zeichnen
  // einer Maske kommen die Meldungen einzeln, das Ausmessen soll es nicht.
  const abarbeiten = () => {
    geplant = false;
    const punkte = [...offen];
    offen.clear();
    if (!telefon()) return;
    for (const punkt of punkte) {
      if (!punkt.isConnected || !punkt.matches(GEWAEHLT)) continue;
      const leiste = leisteVon(punkt);
      if (leiste) insBild(leiste, punkt);
    }
  };
  const merken = (el: HTMLElement) => {
    offen.add(el);
    if (!geplant) { geplant = true; requestAnimationFrame(abarbeiten); }
  };

  const beobachter = new MutationObserver((meldungen) => {
    if (!telefon()) return;
    for (const m of meldungen) {
      if (m.type === "attributes") {
        if (m.target instanceof HTMLElement) merken(m.target);
        continue;
      }
      m.addedNodes.forEach((knoten) => {
        if (!(knoten instanceof HTMLElement)) return;
        if (knoten.matches(GEWAEHLT)) merken(knoten);
        knoten.querySelectorAll<HTMLElement>(GEWAEHLT).forEach(merken);
      });
    }
  });
  beobachter.observe(document.body, {
    subtree: true, childList: true,
    attributes: true, attributeFilter: ["aria-pressed", "aria-selected", "aria-current"],
  });
  return () => beobachter.disconnect();
}

/** Steht links noch etwas? Steht rechts noch etwas? */
export type Rollrand = { links: boolean; rechts: boolean };

const KEIN_RAND: Rollrand = { links: false, rechts: false };

/**
 * Misst an einer Leiste, die zur Seite rollt, ob **außerhalb des Bildes noch
 * etwas steht** — links, rechts, beides oder nichts. Vier Fälle: Die Leiste
 * passt ganz ins Bild (nichts), steht ganz links (rechts), mittendrin
 * (beides), ganz rechts (links).
 *
 * Ein Pixel Spiel: Browser rechnen `scrollLeft` gebrochen, und eine Leiste,
 * die ganz rechts steht, meldete sonst „da kommt noch was".
 */
export function useRollrand(ref: RefObject<HTMLElement | null>): Rollrand {
  const [rand, setzeRand] = useState<Rollrand>(KEIN_RAND);

  const messen = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rest = el.scrollWidth - el.clientWidth - el.scrollLeft;
    setzeRand((war) => {
      const neu = { links: el.scrollLeft > 1, rechts: rest > 1 };
      return war.links === neu.links && war.rechts === neu.rechts ? war : neu;
    });
  }, [ref]);

  /* `useLayoutEffect` ohne Abhängigkeiten: nach jedem Zeichnen, damit der
     Rand schon im ersten Bild richtig steht — ein Zeichen, das nach dem
     Zeichnen aufblendet, ist eine Bewegung, die niemand bestellt hat. */
  useLayoutEffect(messen);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", messen, { passive: true });

    /* Die Leiste **und** ihre Punkte: Die Leiste allein wird nicht breiter,
       wenn ein Zähler von `3` auf `12` wächst oder die Schrift nachlädt —
       ihr Inhalt schon. Kommt ein Punkt dazu oder geht einer, werden die
       Kinder neu beobachtet. */
    const groesse = new ResizeObserver(messen);
    const kinderBeobachten = () => {
      groesse.disconnect();
      groesse.observe(el);
      for (const kind of Array.from(el.children)) groesse.observe(kind);
    };
    kinderBeobachten();
    const kinder = new MutationObserver(() => { kinderBeobachten(); messen(); });
    kinder.observe(el, { childList: true });

    return () => {
      el.removeEventListener("scroll", messen);
      groesse.disconnect();
      kinder.disconnect();
    };
  }, [ref, messen]);

  return rand;
}
